import React, { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeft, ChevronLeft, ChevronRight, CreditCard, Gamepad2 } from 'lucide-react';
import client from '../lib/api';
import DateRangeFilter from './components/DateRangeFilter';
import WagerCheckPanel from './components/WagerCheckPanel';
import { AdminError, AdminSkeleton } from './components/PageState';
import { EMPTY_DATE_RANGE, dateRangeParams } from './lib/dateRange';
import PageSizeSelect from './components/PageSizeSelect';
import { readPageSize, writePageSize } from './lib/pagination';
import { formatDateTime, formatMoney } from './lib/formatters';
import { transactionActor } from './lib/transactions';
import HostedBetSelections from './components/HostedBetSelections';
import { PENDING_HINT, hostedBadge } from './lib/hostedBets';
import { useHostedBetDetails } from './lib/useHostedBetDetails';

// Sayfa boyutu artık sabit değil: yönetici satır sayısını seçer ve tercih
// tarayıcısında kalır (bkz. lib/pagination).
const PAGE_SCOPE = 'user-activity';
const transactionLabels = {
  deposit: 'Yatırım', withdraw: 'Çekim', bonus: 'Bonus', promo: 'Promosyon',
  cashback: 'Kayıp Bonusu', bet: 'Bahis', win: 'Kazanç',
  casino_bet: 'Casino Bahsi', casino_win: 'Casino Kazancı',
  casino_refund: 'Casino İadesi',
};
const statusLabels = {
  onaylandi: 'Onaylandı', reddedildi: 'Reddedildi', beklemede: 'Beklemede',
};
// Süzgeç anahtarları backend'deki TRANSACTION_GROUPS ile aynı olmalıdır
// (backend/routers/admin_router.py); ikisi ayrışırsa uç 400 döner.
const groupFilters = [
  { id: 'all', label: 'Tümü' },
  { id: 'sports', label: 'Spor' },
  { id: 'casino', label: 'Slot / Casino' },
  { id: 'deposit', label: 'Para Yatırma' },
  { id: 'withdraw', label: 'Para Çekme' },
  { id: 'bonus', label: 'Bonus' },
];
// Casino turu ile spor kuponu ayrı tablolardadır (game_rounds / coupons) ve
// ayrı uçlardan okunur; bahis geçmişi ekranı ikisini sekmeyle gösterir.
const betTabs = [
  { id: 'casino', label: 'Slot / Casino' },
  // Hazır spor kitabı (Flexrix / Betby) kuponları da bu sekmededir: kupon
  // sağlayıcıda durup bize yalnızca para hareketi gelse de yöneticinin
  // sorusu tektir — "bu üye spora ne oynamış".
  { id: 'sports', label: 'Spor Kuponları' },
];
const couponStatusLabels = {
  active: { label: 'Beklemede', cls: 'badge-yellow' },
  won: { label: 'Kazandı', cls: 'badge-green' },
  lost: { label: 'Kaybetti', cls: 'badge-red' },
  cashed_out: { label: 'Cashout', cls: 'badge-yellow' },
};
const marketLabels = {
  POINT_SPREAD: 'Handikap', POINT_TOTAL: 'Toplam', MONEYLINE: 'Maç Sonucu',
};
// Seçim adı BetCoupons.jsx ile aynı mantıkla çözülür: konsensüs kaynakları
// (sportsbook / oddsapi) market türünü ve hazır etiketi yazar, API-Football
// yalnızca 1/X/2 verir.
const pickLabel = (pick) => (
  `${marketLabels[pick.market_type] || 'Maç Sonucu'}: ${pick.label || pick.pick_key}`
);
const pickResultClass = {
  won: 'text-emerald-400', lost: 'text-rose-400', void: 'text-slate-500',
};

const UserActivity = ({ kind }) => {
  const { userId } = useParams();
  const [user, setUser] = useState(null);
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [summary, setSummary] = useState(null);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(() => readPageSize(PAGE_SCOPE));
  const [group, setGroup] = useState('all');
  const [status, setStatus] = useState('all');
  const [betTab, setBetTab] = useState('casino');
  const [couponStatus, setCouponStatus] = useState('all');
  const [dateRange, setDateRange] = useState(EMPTY_DATE_RANGE);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const games = kind === 'games';
  const sports = games && betTab === 'sports';

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      // Tarih süzgeci üç ucun da ortak dilidir; tür/durum süzgeçleri uca göre
      // değişir: finans listesi tür + durum, kupon listesi yalnızca durum,
      // casino turları hiçbirini tanımaz.
      const params = {
        skip: page * pageSize,
        limit: pageSize,
        ...dateRangeParams(dateRange),
      };
      if (!games) {
        if (group !== 'all') params.group = group;
        if (status !== 'all') params.status = status;
      } else if (sports && couponStatus !== 'all') {
        params.status = couponStatus;
      }
      const endpoint = games ? (sports ? 'coupons' : 'games') : 'transactions';
      const [{ data: profile }, { data }] = await Promise.all([
        client.get(`/admin/users/${userId}`),
        client.get(`/admin/users/${userId}/${endpoint}`, { params }),
      ]);
      setUser(profile);
      setItems(data.items || []);
      setTotal(data.total || 0);
      setSummary(data.summary || null);
    } catch (requestError) {
      setError(requestError.response?.data?.detail || 'Hareketler alınamadı');
    } finally { setLoading(false); }
  }, [couponStatus, dateRange, games, group, page, pageSize, sports, status,
      userId]);

  useEffect(() => { load(); }, [load]);

  // Hazır spor kitabı kuponunun içi para hareketiyle gelmez; eksik olanlar
  // sağlayıcıdan tek tek istenir. Kanca sitenin kendi kuponlarına dokunmaz.
  const { busy, errors, retry } = useHostedBetDetails(items, setItems);

  if (loading && !user) return <AdminSkeleton rows={10} />;
  if (error && !user) return <AdminError message={error} onRetry={load} />;
  const pageCount = Math.max(1, Math.ceil(total / pageSize));
  // Boyut değişince sayfa başa alınır: 25 satırın 8. sayfasındayken 500'e
  // geçmek, toplamın dışında kalan boş bir sayfa gösterirdi.
  const changePageSize = (size) => {
    setPage(0);
    setPageSize(size);
    writePageSize(PAGE_SCOPE, size);
  };
  // Tarih süzgeci üç listede de aynıdır (casino turu, spor kuponu, finans
  // hareketi); tek yerde kurulup her iki süzgeç kartına konur.
  const dateFilterRow = (
    <div className="mt-2 w-full border-t border-white/5 pt-2">
      <DateRangeFilter
        value={dateRange}
        onChange={(value) => { setPage(0); setDateRange(value); }}
      />
    </div>
  );

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center gap-3">
        <Link to={`/admin/users/${userId}`} className="icon-btn view"><ArrowLeft size={16} /></Link>
        <div className={`grid h-11 w-11 place-items-center rounded-xl border ${games ? 'border-fuchsia-400/20 bg-fuchsia-400/10 text-fuchsia-300' : 'border-emerald-400/20 bg-emerald-400/10 text-emerald-300'}`}>{games ? <Gamepad2 size={18} /> : <CreditCard size={18} />}</div>
        <div><div className="text-xs font-semibold text-cyan-400">@{user?.username}</div><h1 className="text-xl font-bold">{games ? 'Bahis Geçmişi' : 'Tüm Finans İşlemleri'}</h1></div>
        <div className="ml-auto flex items-center gap-2">
          <Link to={`/admin/users/${userId}/games`} className={`admin-btn-secondary ${games ? '!border-fuchsia-400/30 !text-fuchsia-300' : ''}`}>Bahis Geçmişi</Link>
          <Link to={`/admin/users/${userId}/transactions`} className={`admin-btn-secondary ${!games ? '!border-emerald-400/30 !text-emerald-300' : ''}`}>Finans İşlemleri</Link>
          <span className="ml-2 text-xs text-slate-500">Toplam {total} {games ? (sports ? 'kupon' : 'bahis') : 'kayıt'}</span>
        </div>
      </div>

      {games && (
        <div className="admin-card flex flex-wrap items-center gap-2 p-3">
          {betTabs.map((tab) => (
            <button
              key={tab.id}
              type="button"
              onClick={() => { setPage(0); setBetTab(tab.id); }}
              className={`admin-tab ${betTab === tab.id ? 'active' : ''}`}
              aria-pressed={betTab === tab.id}
            >
              {tab.label}
            </button>
          ))}
          {sports && (
            <select
              value={couponStatus}
              onChange={(event) => { setPage(0); setCouponStatus(event.target.value); }}
              className="admin-input admin-input-w-lg ml-auto"
              aria-label="Kupon durumu"
            >
              <option value="all">Tüm durumlar</option>
              <option value="active">Beklemede</option>
              <option value="won">Kazandı</option>
              <option value="lost">Kaybetti</option>
              <option value="cashed_out">Cashout</option>
            </select>
          )}
          {dateFilterRow}
        </div>
      )}

      {/* Çevrim denetimi: son yatırım ve alınan bonuslar bahislerin hemen
          üstünde durur; "bu yatırımdan sonrası" süzgeci tarih alanını
          yatırımın dakikasına kurar. */}
      {games && (
        <WagerCheckPanel
          userId={userId}
          onFilterSince={({ date, time }) => {
            setPage(0);
            setDateRange({
              range: 'custom', from: date, fromTime: time, to: '', toTime: '',
            });
          }}
        />
      )}

      {sports && summary && (
        <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
          <div className="admin-card p-4"><div className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Toplam Bahis</div><div className="mt-1 text-lg font-black text-white">{formatMoney(summary.total_stake)}</div></div>
          <div className="admin-card p-4"><div className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Toplam Ödeme</div><div className="mt-1 text-lg font-black text-emerald-400">{formatMoney(summary.total_payout)}</div></div>
          <div className="admin-card p-4"><div className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Net Sonuç</div><div className={`mt-1 text-lg font-black ${Number(summary.net) >= 0 ? 'text-emerald-400' : 'text-rose-400'}`} title="Freebet kuponlarında bahis tutarı kasadan çıkmadığı için nete girmez">{formatMoney(summary.net)}</div></div>
          <div className="admin-card p-4"><div className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Kupon Sonuçları</div><div className="mt-1 text-sm font-bold"><span className="text-emerald-400">{summary.won} kazandı</span><span className="mx-2 text-slate-700">•</span><span className="text-rose-400">{summary.lost} kaybetti</span>{Number(summary.active) > 0 && <><span className="mx-2 text-slate-700">•</span><span className="text-amber-400">{summary.active} bekliyor</span></>}{Number(summary.cashed_out) > 0 && <><span className="mx-2 text-slate-700">•</span><span className="text-yellow-400">{summary.cashed_out} cashout</span></>}</div></div>
        </div>
      )}

      {games && !sports && summary && (
        <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
          <div className="admin-card p-4"><div className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Toplam Bahis</div><div className="mt-1 text-lg font-black text-white">{formatMoney(summary.total_bet)}</div></div>
          <div className="admin-card p-4"><div className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Toplam Kazanç</div><div className="mt-1 text-lg font-black text-emerald-400">{formatMoney(summary.total_win)}</div></div>
          <div className="admin-card p-4"><div className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Net Sonuç</div><div className={`mt-1 text-lg font-black ${Number(summary.net) >= 0 ? 'text-emerald-400' : 'text-rose-400'}`}>{formatMoney(summary.net)}</div></div>
          <div className="admin-card p-4"><div className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Tur Sonuçları</div><div className="mt-1 text-sm font-bold"><span className="text-emerald-400">{summary.won} kazandı</span><span className="mx-2 text-slate-700">•</span><span className="text-rose-400">{summary.lost} kaybetti</span>{Number(summary.pending) > 0 && <><span className="mx-2 text-slate-700">•</span><span className="text-amber-400" title="Sağlayıcı bu turlar için henüz kazanç bildirmedi; kısa süre sonra kazandı/kaybetti olarak kapanırlar.">{summary.pending} tur açık</span></>}</div></div>
        </div>
      )}

      {!games && (
        <div className="admin-card flex flex-wrap items-center gap-2 p-3">
          {groupFilters.map((filter) => (
            <button
              key={filter.id}
              type="button"
              onClick={() => { setPage(0); setGroup(filter.id); }}
              className={`admin-tab ${group === filter.id ? 'active' : ''}`}
              aria-pressed={group === filter.id}
            >
              {filter.label}
            </button>
          ))}
          <select
            value={status}
            onChange={(event) => { setPage(0); setStatus(event.target.value); }}
            className="admin-input admin-input-w-lg ml-auto"
            aria-label="İşlem durumu"
          >
            <option value="all">Tüm durumlar</option>
            <option value="onaylandi">Onaylandı</option>
            <option value="reddedildi">Reddedildi</option>
            <option value="beklemede">Beklemede</option>
          </select>
          {dateFilterRow}
        </div>
      )}

      <section className="admin-card overflow-hidden">
        <div className="overflow-x-auto">
          {sports ? (
            <table className="admin-table w-full min-w-[1180px]">
              <thead><tr><th>TARİH</th><th>KAYNAK</th><th>SEÇİMLER</th><th>KUPON NO</th><th>BAHİS TUTARI</th><th>TOPLAM ORAN</th><th>ÖDEME</th><th>NET</th><th>DURUM</th></tr></thead>
              <tbody>
                {items.map((coupon) => {
                  // Hazır spor kitabı kuponu (Flexrix / Betby) aynı listededir:
                  // kupon sağlayıcıda durur ve bize yalnızca para hareketi
                  // gelir, ama yöneticinin sorusu tektir.
                  if (coupon.source === 'hosted') {
                    const hosted = hostedBadge(coupon.status);
                    return (
                      <tr key={coupon.id}>
                        <td className="whitespace-nowrap text-xs text-slate-500">{formatDateTime(coupon.created_at)}</td>
                        <td>
                          <span className="inline-flex rounded-md border border-violet-400/25 bg-violet-400/10 px-2 py-1 text-[10px] font-black uppercase tracking-wide text-violet-300">{coupon.provider_label}</span>
                          {coupon.is_live && <div className="mt-0.5 text-[10px] font-black uppercase tracking-wide text-rose-300">Canlı</div>}
                        </td>
                        <td className="min-w-[340px] py-2"><HostedBetSelections item={coupon} loading={!!busy[coupon.id]} error={errors[coupon.id] || ''} onRetry={retry} /></td>
                        <td className="max-w-[150px] truncate font-mono text-[10px] text-slate-600" title={coupon.bet_id}>{coupon.bet_id}</td>
                        <td className="whitespace-nowrap font-bold text-white">{formatMoney(coupon.stake)}</td>
                        <td className="font-bold text-slate-200">{Number(coupon.odds) > 0 ? Number(coupon.odds).toFixed(2) : <span className="text-slate-600" title="Oran sağlayıcının kupon bildiriminden gelir; bildirim kapalıysa bilinmez">—</span>}</td>
                        <td className={Number(coupon.payout) > 0 ? 'font-bold text-emerald-400' : 'text-slate-500'}>{formatMoney(coupon.status === 'void' ? coupon.refund : coupon.payout)}</td>
                        <td className={Number(coupon.net) >= 0 ? 'font-bold text-emerald-400' : 'font-bold text-rose-400'}>{coupon.status === 'pending' ? <span className="text-slate-600">—</span> : formatMoney(coupon.net)}</td>
                        <td><span className={`badge-pill ${hosted.cls}`} title={coupon.status === 'pending' ? PENDING_HINT : undefined}>{hosted.label}</span></td>
                      </tr>
                    );
                  }
                  const badge = couponStatusLabels[coupon.status] || couponStatusLabels.active;
                  const settled = coupon.status === 'won' || coupon.status === 'cashed_out';
                  return (
                    <tr key={coupon.id}>
                      <td className="whitespace-nowrap text-xs text-slate-500">{formatDateTime(coupon.created_at)}</td>
                      <td><span className="inline-flex rounded-md border border-sky-400/20 bg-sky-400/10 px-2 py-1 text-[10px] font-black uppercase tracking-wide text-sky-300">Spor</span></td>
                      <td className="min-w-[340px] space-y-1 py-2">
                        {(coupon.picks || []).map((pick, index) => (
                          <div key={`${coupon.id}-${index}`} className="flex flex-wrap items-baseline gap-x-2 text-xs">
                            <span className="font-semibold text-white">{pick.home} - {pick.away}</span>
                            <span className={pickResultClass[pick.result] || 'text-slate-400'}>{pickLabel(pick)}</span>
                            <span className="font-bold text-slate-300">{Number(pick.odd).toFixed(2)}</span>
                            {pick.score && <span className="text-slate-500">({pick.score})</span>}
                            {pick.league && <span className="text-[10px] text-slate-600">{pick.league}</span>}
                          </div>
                        ))}
                        {!(coupon.picks || []).length && <span className="text-xs text-slate-600">Seçim bilgisi yok</span>}
                      </td>
                      <td className="max-w-[150px] truncate font-mono text-[10px] text-slate-600" title={coupon.id}>{coupon.id}</td>
                      <td className="whitespace-nowrap font-bold text-white">{formatMoney(coupon.stake)}{coupon.freebet && <span className="ml-1 inline-flex rounded-md border border-cyan-400/25 bg-cyan-400/10 px-2 py-1 text-[10px] font-black uppercase tracking-wide text-cyan-300" title="Freebet hakkıyla oynandı: bakiyeden para düşülmedi, kazançta bahis tutarı iade edilmez">Freebet</span>}</td>
                      <td className="font-bold text-slate-200">{Number(coupon.total_odd).toFixed(2)}</td>
                      <td className={settled && Number(coupon.payout) > 0 ? 'font-bold text-emerald-400' : 'text-slate-500'}>{settled ? formatMoney(coupon.payout) : coupon.status === 'lost' ? formatMoney(0) : <span title="Kupon sonuçlanmadı; gösterilen tutar muhtemel kazançtır">{formatMoney(coupon.possible_win)}</span>}</td>
                      <td className={Number(coupon.pnl) >= 0 ? 'font-bold text-emerald-400' : 'font-bold text-rose-400'}>{coupon.status === 'active' ? <span className="text-slate-600">—</span> : formatMoney(coupon.pnl)}</td>
                      <td><span className={`badge-pill ${badge.cls}`}>{badge.label}</span></td>
                    </tr>
                  );
                })}
                {!items.length && <tr><td colSpan={9} className="py-12 text-center text-slate-500">Spor kuponu bulunmuyor.</td></tr>}
              </tbody>
            </table>
          ) : games ? (
            <table className="admin-table w-full min-w-[1180px]"><thead><tr><th>TARİH</th><th>KAYNAK</th><th>SAĞLAYICI / OYUN</th><th>TUR NO</th><th>BAHİS TUTARI</th><th>KAZANÇ</th><th>NET</th><th>KALAN BAKİYE</th><th>DURUM</th></tr></thead><tbody>{items.map((item) => <tr key={item.id}><td className="whitespace-nowrap text-xs text-slate-500">{formatDateTime(item.created_at)}</td><td><span className="inline-flex rounded-md border border-orange-400/20 bg-orange-400/10 px-2 py-1 text-[10px] font-black uppercase tracking-wide text-orange-300">Casino</span></td><td><div className="font-semibold text-white">{item.game_name || item.game_code || 'Oyun'}</div><div className="mt-0.5 text-[10px] text-slate-500">{[item.provider_name, item.studio_name].filter(Boolean).join(' • ') || '-'}</div></td><td className="max-w-[150px] truncate font-mono text-[10px] text-slate-600" title={item.round_id}>{item.round_id || '-'}</td><td className="font-bold text-white">{item.free_round ? <span className="inline-flex rounded-md border border-cyan-400/25 bg-cyan-400/10 px-2 py-1 text-[10px] font-black uppercase tracking-wide text-cyan-300" title="Sıfır bahisli kampanya turu (freespin)">Ücretsiz</span> : formatMoney(item.bet)}</td><td className={Number(item.win) > 0 ? 'font-bold text-emerald-400' : 'text-slate-600'}>{Number(item.win) > 0 ? `+${formatMoney(item.win)}` : formatMoney(0)}</td><td className={Number(item.pnl) >= 0 ? 'font-bold text-emerald-400' : 'font-bold text-rose-400'}>{formatMoney(item.pnl)}</td><td className="whitespace-nowrap font-semibold text-slate-200">{item.balance_after === null || item.balance_after === undefined ? <span className="text-slate-600" title="Bu bahis için kalan bakiye kaydı bulunmuyor">—</span> : formatMoney(item.balance_after)}</td><td><span className={`badge-pill ${item.result === 'won' ? 'badge-green' : item.result === 'lost' ? 'badge-red' : 'badge-yellow'}`} title={item.result === 'pending' ? 'Tur açık: sağlayıcı henüz kazanç bildirmedi. Bonus turu sürüyor olabilir; birkaç dakika içinde kazanç gelmezse kayıp sayılır.' : undefined}>{item.result === 'won' ? 'Kazandı' : item.result === 'lost' ? 'Kaybetti' : 'Tur açık'}</span></td></tr>)}{!items.length && <tr><td colSpan={9} className="py-12 text-center text-slate-500">Bahis geçmişi bulunmuyor.</td></tr>}</tbody></table>
          ) : (
            <table className="admin-table w-full min-w-[1120px]"><thead><tr><th>TÜR</th><th>YÖNTEM</th><th>TUTAR</th><th>KALAN BAKİYE</th><th>DURUM</th><th>İŞLEMİ YAPAN</th><th>NOT</th><th>İŞLEM NO</th><th>TARİH</th></tr></thead><tbody>{items.map((item) => <tr key={item.id}><td className="font-semibold text-white">{transactionLabels[item.type] || item.type}</td><td className="text-slate-400">{item.method || '-'}</td><td className={Number(item.amount) >= 0 ? 'font-bold text-emerald-400' : 'font-bold text-rose-400'}>{formatMoney(item.amount)}</td><td className="whitespace-nowrap font-semibold text-slate-200">{item.balance_after === null || item.balance_after === undefined ? <span className="text-slate-600" title="Bu hareket, bakiye kaydı tutulmaya başlamadan önce oluşmuş">—</span> : formatMoney(item.balance_after)}</td><td><span className={`badge-pill ${item.status === 'onaylandi' ? 'badge-green' : item.status === 'reddedildi' ? 'badge-red' : 'badge-yellow'}`}>{statusLabels[item.status] || item.status}</span></td><td className="whitespace-nowrap text-xs">{transactionActor(item) ? <span className="font-semibold text-violet-300">{transactionActor(item)}</span> : <span className="text-slate-600" title="Üye talebi veya sağlayıcı hareketi; yetkili işlemi değil">—</span>}</td><td className="max-w-[280px] text-xs text-slate-500">{item.note || '-'}</td><td className="font-mono text-[10px] text-slate-600">{item.id}</td><td className="text-xs text-slate-500">{formatDateTime(item.created_at)}</td></tr>)}{!items.length && <tr><td colSpan={9} className="py-12 text-center text-slate-500">Seçili süzgeçlere uyan finans işlemi bulunmuyor.</td></tr>}</tbody></table>
          )}
        </div>
        <div className="flex flex-wrap items-center justify-between gap-3 border-t border-white/5 px-4 py-3">
          <button disabled={page === 0 || loading} onClick={() => setPage((value) => Math.max(0, value - 1))} className="admin-btn-secondary inline-flex items-center gap-2 disabled:opacity-40"><ChevronLeft size={14} /> Önceki</button>
          <div className="flex flex-wrap items-center justify-center gap-3">
            <span className="text-xs text-slate-500">Sayfa {page + 1} / {pageCount}</span>
            <PageSizeSelect value={pageSize} onChange={changePageSize} disabled={loading} />
          </div>
          <button disabled={page + 1 >= pageCount || loading} onClick={() => setPage((value) => value + 1)} className="admin-btn-secondary inline-flex items-center gap-2 disabled:opacity-40">Sonraki <ChevronRight size={14} /></button>
        </div>
      </section>
    </div>
  );
};

export default UserActivity;
